import * as React from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import Paper from "@material-ui/core/Paper";
import Divider from "@material-ui/core/Divider";
import { connect } from "react-redux";
import { ApplicationState } from "../../redux/reducers";
import { ArrowRightAlt } from '@material-ui/icons';
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { traducirLabel } from "../../blocks/traductor";
import { enums } from "../../enums/enums";
import { ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { IGuiaWizard, seccionAlcanzadaActual } from "../../entities/guias/IGuiaWizard";
import {
    nombreServicioAdicional,
    textoEadEntrega,
    textoFechaModalidadRecoleccion,
    textoRadFormaEnvio
} from "./revisionGuiaResumenHelpers";

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            width: '100%',
            '& td': {
                paddingLeft: "0"
            }
        },
        header: {
            boxSizing: "border-box",
            height: "60.52px",
            backgroundColor: "#F7F7F7",
            boxShadow: "0 2px 14px 0 rgba(0,0,0,0.08)"
        },
        mainTitle: {
            color: "#19754e",
            fontSize: "20px"
        },
        title: {
            color: "#19754e",
            fontSize: "10px",
            fontWeight: "bold",
            marginTop: "2em",
            marginBottom: "0.2em",
            '& td': {
                paddingTop: "2em",
                paddingBottom: "0.2em"
            }
        },
        information: {
            color: "#212121",
            fontSize: "14px",
            fontWeight: 500
        },
        additional: {
            color: "rgba(49,49,49,0.87)",
            fontSize: "12px"
        },
        sectionTitle: {
            color: "#19754e",
            fontSize: "10px",
            fontWeight: "bold",
            marginTop: "1.5em",
            marginBottom: "0.3em"
        },
        sectionText: {
            color: "#212121",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: 1.4
        }
    }),
);

const NOMBRES_CARGO_CITA_RAD = new Set([
    "CARGO POR CITA RAD",
    "FLETE CARGO RAD FUERA DE HORARIO",
    "CARGO POR CITA RAD CEDIS"
]);

const NOMBRES_CARGO_CITA_EAD = new Set([
    "CARGO POR CITA EAD",
    "FLETE CARGO EAD FUERA DE HORARIO",
    "CARGO POR CITA EAD CEDIS"
]);

const IDS_SERVICIO_MODALIDAD = new Set([9, 47, 6, 12]);

const NOMBRES_CARGO_MODALIDAD = new Set([
    ...NOMBRES_CARGO_CITA_RAD,
    ...NOMBRES_CARGO_CITA_EAD
]);

interface StateProps {
    guiaOrigen: IRecoleccion;
    guiaDestino: IRecoleccion;
    guiaWizard: IGuiaWizard;
    guiaServicios: ServicioAdicional[];
}

type Props = StateProps;

const textoOrigenDestino = (recoleccion: IRecoleccion): string => {
    if (!recoleccion?.distribuidor && !recoleccion?.codigoPostal) {
        return "";
    }
    const codigoPostal = recoleccion.codigoPostal || recoleccion.colonia?.codigoPostal || "";
    const ciudad = recoleccion.sucursal?.ciudad?.nombre || recoleccion.colonia?.ciudad?.nombre || "";
    const estado = recoleccion.sucursal?.estado?.nombre || recoleccion.colonia?.estado?.nombre || "";
    return `${codigoPostal} ${ciudad} ${estado}`.trim();
};

const nombreServicioCanonico = (servicio: ServicioAdicional): string =>
    (servicio.nombre || servicio.concepto?.nombreConcepto || "").trim().toUpperCase();

const esServicioModalidadRecoleccionEntrega = (servicio: ServicioAdicional): boolean =>
    IDS_SERVICIO_MODALIDAD.has(servicio.id)
    || NOMBRES_CARGO_MODALIDAD.has(nombreServicioCanonico(servicio));

const renderServicioCita = (
    tipo: "RAD" | "EAD",
    servicios?: ServicioAdicional[],
    tipoRecoleccionId?: number
): React.ReactNode => {
    const idiomaEn = localStorage.getItem("idioma") === "en-US";
    const nombresCargo = tipo === "RAD"
        ? {
            normal: "CARGO POR CITA RAD",
            fueraHorario: "FLETE CARGO RAD FUERA DE HORARIO",
            cedis: "CARGO POR CITA RAD CEDIS"
        }
        : {
            normal: "CARGO POR CITA EAD",
            fueraHorario: "FLETE CARGO EAD FUERA DE HORARIO",
            cedis: "CARGO POR CITA EAD CEDIS"
        };
    const nombreMostrar = tipoRecoleccionId === 2
        ? nombresCargo.normal
        : tipoRecoleccionId === 3
            ? nombresCargo.fueraHorario
            : tipoRecoleccionId === 4
                ? nombresCargo.cedis
                : undefined;

    if (!nombreMostrar) {
        return null;
    }

    const nombresDeCita = [
        nombresCargo.normal,
        nombresCargo.fueraHorario,
        nombresCargo.cedis
    ];
    const serviciosSeleccionados = (servicios ?? []).filter(
        servicio =>
            servicio.seleccionado === true
            && nombresDeCita.includes(nombreServicioCanonico(servicio))
    );
    const servicioCita = serviciosSeleccionados.find(
        servicio => nombreServicioCanonico(servicio) === nombreMostrar
    ) ?? serviciosSeleccionados[0];

    if (!servicioCita) {
        return null;
    }

    const etiqueta = nombreServicioCanonico(servicioCita) === nombreMostrar
        ? servicioCita.etiquetaServicioAdicionalDto?.nombre
        : undefined;

    return (
        <li key={`${tipo}-${servicioCita.id ?? nombreMostrar}`}>
            {idiomaEn ? (etiqueta ?? nombreMostrar) : nombreMostrar}
        </li>
    );
};

const textoTipoSeguro = (guiaWizard: IGuiaWizard): string => {
    if (guiaWizard.esSeguroBasico) {
        return traducirLabel(273);
    }
    if (guiaWizard.esSeguroMercancia) {
        return traducirLabel(274);
    }
    return "";
};

const ResumenGuia = ({ guiaOrigen, guiaDestino, guiaWizard, guiaServicios }: Props) => {
    const classes = useStyles();
    const origen = textoOrigenDestino(guiaOrigen);
    const destino = textoOrigenDestino(guiaDestino);
    const nombreOrigen = guiaOrigen.distribuidor?.nombre || guiaOrigen.distribuidor?.nombreClienteDistribuidor || "";
    const nombreDestino = guiaDestino.distribuidor?.nombre || guiaDestino.distribuidor?.nombreClienteDistribuidor || "";
    const contactoOrigen = guiaOrigen.contacto;
    const contactoDestino = guiaDestino.contacto;

    const formaEnvio = guiaOrigen.tipoRecoleccionId ? textoRadFormaEnvio(guiaOrigen) : "";
    const entrega = guiaDestino.tipoRecoleccionId ? textoEadEntrega(guiaDestino) : "";

    const fechaRad = guiaOrigen.tipoRecoleccionId !== enums.tipoRecoleccion.ocurre
        ? textoFechaModalidadRecoleccion(guiaOrigen, true)
        : "";
    const fechaEad = guiaDestino.tipoRecoleccionId !== enums.tipoRecoleccion.ocurre
        ? textoFechaModalidadRecoleccion(guiaDestino, false)
        : "";

    const seccionAlcanzada = seccionAlcanzadaActual(guiaWizard);
    const tipoSeguro = textoTipoSeguro(guiaWizard);
    const mostrarSeguro = seccionAlcanzada >= 2 && tipoSeguro;

    const serviciosAdicionalesSeleccionados = (guiaServicios ?? []).filter(
        s => s.seleccionado === true && !esServicioModalidadRecoleccionEntrega(s)
    );
    const mostrarServiciosAdicionales = seccionAlcanzada >= 3 && serviciosAdicionalesSeleccionados.length > 0;

    const mostrarDetalleModalidad = guiaOrigen.tipoRecoleccionId !== undefined
        || guiaDestino.tipoRecoleccionId !== undefined;

    return (
        <Paper>
            <Box className={classes.header} display="flex" alignItems="center">
                <Box className={classes.mainTitle} px={3}>
                    {traducirLabel(264)}
                </Box>
            </Box>
            <Box px={3} pb={2}>
                <table className={classes.root}>
                    <tbody>
                        <tr className={classes.title}>
                            <td style={{ width: "45%" }}>{traducirLabel(222)}</td>
                            <td style={{ width: "10%" }} />
                            <td style={{ width: "45%" }}>{traducirLabel(86)}</td>
                        </tr>
                        <tr style={{ fontSize: "14px", color: "rgba(0,0,0,0.87)", fontWeight: 500 }}>
                            <td style={{ width: "45%" }}>
                                <div style={{ maxWidth: "120px", overflowWrap: "break-word" }}>{origen}</div>
                            </td>
                            <td style={{ width: "10%" }}>
                                {(origen || destino) &&
                                    <ArrowRightAlt style={{ verticalAlign: "middle" }} />
                                }
                            </td>
                            <td style={{ width: "45%" }}>
                                <div style={{ maxWidth: "120px", overflowWrap: "break-word" }}>{destino}</div>
                            </td>
                        </tr>
                        <tr style={{ fontSize: "13px", color: "rgba(0,0,0,0.87)", fontWeight: 700, paddingTop: "5px" }}>
                            <td style={{ width: "45%" }}>
                                <div style={{ maxWidth: "120px", overflowWrap: "break-word" }}>{nombreOrigen}</div>
                            </td>
                            <td style={{ width: "10%" }} />
                            <td style={{ width: "45%" }}>
                                <div style={{ maxWidth: "120px", overflowWrap: "break-word" }}>{nombreDestino}</div>
                            </td>
                        </tr>
                        {contactoOrigen?.nombre && (
                            <tr style={{ fontSize: "13px", color: "rgba(0,0,0,0.87)", fontWeight: 700 }}>
                                <td style={{ width: "45%" }}>
                                    <div style={{ maxWidth: "120px", overflowWrap: "break-word" }}>
                                        {contactoOrigen.nombre}
                                    </div>
                                </td>
                                <td style={{ width: "10%" }} />
                                {contactoDestino?.nombre ? (
                                    <td style={{ width: "45%" }}>
                                        <div style={{ maxWidth: "120px", overflowWrap: "break-word" }}>
                                            {contactoDestino.nombre}
                                        </div>
                                    </td>
                                ) : (
                                    <td style={{ width: "45%" }} />
                                )}
                            </tr>
                        )}
                        {!contactoOrigen?.nombre && contactoDestino?.nombre && (
                            <tr style={{ fontSize: "13px", color: "rgba(0,0,0,0.87)", fontWeight: 700 }}>
                                <td style={{ width: "45%" }} />
                                <td style={{ width: "10%" }} />
                                <td style={{ width: "45%" }}>
                                    <div style={{ maxWidth: "120px", overflowWrap: "break-word" }}>
                                        {contactoDestino.nombre}
                                    </div>
                                </td>
                            </tr>
                        )}
                        {(contactoOrigen?.correo || contactoDestino?.correo) && (
                            <tr style={{ fontSize: "11px", color: "rgba(0,0,0,0.87)", fontWeight: 600 }}>
                                {contactoOrigen?.correo ? (
                                    <td style={{ width: "45%" }}>
                                        <div style={{ width: "150px", wordBreak: "break-all" }}>
                                            {contactoOrigen.correo}
                                        </div>
                                    </td>
                                ) : (
                                    <td style={{ width: "45%" }} />
                                )}
                                <td style={{ width: "10%" }} />
                                {contactoDestino?.correo ? (
                                    <td style={{ width: "45%" }}>
                                        <div style={{ width: "150px", wordBreak: "break-all" }}>
                                            {contactoDestino.correo}
                                        </div>
                                    </td>
                                ) : (
                                    <td style={{ width: "45%" }} />
                                )}
                            </tr>
                        )}
                        {(contactoOrigen?.telefono || contactoDestino?.telefono) && (
                            <tr style={{ fontSize: "11px", color: "rgba(0,0,0,0.87)", fontWeight: 600 }}>
                                {contactoOrigen?.telefono ? (
                                    <td style={{ width: "45%" }}>{contactoOrigen.telefono}</td>
                                ) : (
                                    <td style={{ width: "45%" }} />
                                )}
                                <td style={{ width: "10%" }} />
                                {contactoDestino?.telefono ? (
                                    <td style={{ width: "45%" }}>{contactoDestino.telefono}</td>
                                ) : (
                                    <td style={{ width: "45%" }} />
                                )}
                            </tr>
                        )}
                        {mostrarDetalleModalidad && (formaEnvio || entrega) && (
                            <>
                                <tr className={classes.title}>
                                    <td>{traducirLabel(142)}</td>
                                    <td />
                                    <td>{traducirLabel(114)}</td>
                                </tr>
                                <tr className={classes.information}>
                                    <td>{formaEnvio}</td>
                                    <td />
                                    <td>{entrega}</td>
                                </tr>
                            </>
                        )}
                        {(fechaRad || fechaEad
                            || renderServicioCita("RAD", guiaOrigen.serviciosAdicionales as ServicioAdicional[], guiaOrigen.tipoDeRecoleccionOrigen)
                            || renderServicioCita("EAD", guiaDestino.serviciosAdicionales as ServicioAdicional[], guiaDestino.tipoDeRecoleccionDestino)) && (
                            <tr>
                                <td>
                                    <table>
                                        <tr className={classes.additional}>
                                            <td style={{ verticalAlign: "top" }}>
                                                <ul style={{ margin: 0, paddingLeft: "1.5em" }}>
                                                    {fechaRad && <li>{fechaRad}</li>}
                                                    {renderServicioCita(
                                                        "RAD",
                                                        guiaOrigen.serviciosAdicionales as ServicioAdicional[],
                                                        guiaOrigen.tipoDeRecoleccionOrigen
                                                    )}
                                                </ul>
                                            </td>
                                        </tr>
                                    </table>
                                </td>
                                <td />
                                <td>
                                    <table>
                                        <tr className={classes.additional}>
                                            <td style={{ verticalAlign: "top" }}>
                                                <ul style={{ margin: 0, paddingLeft: "1.5em" }}>
                                                    {fechaEad && <li>{fechaEad}</li>}
                                                    {renderServicioCita(
                                                        "EAD",
                                                        guiaDestino.serviciosAdicionales as ServicioAdicional[],
                                                        guiaDestino.tipoDeRecoleccionDestino
                                                    )}
                                                </ul>
                                            </td>
                                        </tr>
                                    </table>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {(mostrarSeguro || mostrarServiciosAdicionales) && (
                    <Box display="flex" flexDirection="column" style={{ textAlign: "left" }}>
                        <Divider light style={{ marginTop: "1em" }} />
                        <Box className={classes.sectionTitle}>
                            {traducirLabel(278)}
                        </Box>
                        {mostrarSeguro && (
                            <Box className={classes.sectionText}>{tipoSeguro}</Box>
                        )}
                        {mostrarServiciosAdicionales && serviciosAdicionalesSeleccionados.map((servicio, index) => (
                            <Box key={servicio.id ?? index} className={classes.sectionText}>
                                {nombreServicioAdicional(servicio)}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    guiaOrigen: state.guiaOrigen,
    guiaDestino: state.guiaDestino,
    guiaWizard: state.guiaWizard,
    guiaServicios: state.guiaServicios
});

export default connect(mapStateToProps)(ResumenGuia);
